/**
 * Global style imports
 *
 * These must be imported first. Webpack will maintain source order, and we want to be able to override
 *   styles from 3rd party libraries with application styles.
 */

import '@deal/basscss/css/basscss.min.css'
import '@deal/components/es/index.css'
import '@deal/path-flow/es/index.css'
import 'slick-carousel/slick/slick.css'
import 'react-multi-email/style.css'
import 'react-toastify/dist/ReactToastify.css'
import 'react-image-lightbox/style.css'
import 'react-dates/initialize'
import 'react-dates/lib/css/_datepicker.css'
import React, { Component } from 'react'
import { ConsumerTracking } from '@deal/web-tracking/types'
import { withAnalytics, WithAnalyticsProps } from '#src/containers/Analytics'
import { withIdentity, WithIdentityProps } from '#src/containers/Identity'
import { ExpertConversationStartersContextProvider } from '#src/context/conversation-starters'
import DepartmentOpenGraph from '#src/components/DepartmentOpenGraph'
import DefaultOpenGraphImage from './og-image.jpg'
import { DepartmentProvider } from '#src/context/department'
import { hot } from 'react-hot-loader/root'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { ToastContainer } from 'react-toastify'
import { Helmet } from 'react-helmet-async'
import ExpertProfileDraftFullPageTakeover from '#src/components/ExpertProfileDraftFullPageTakeover'
import Router from '#src/routes'
import ScrollToTop from '#src/components/ScrollToTop'
import UnsupportedBrowserBanner from '#src/components/UnsupportedBrowserBanner'
import { UserAgentConsumer } from '#src/containers/UserAgent'
import config from '#src/config'
import styles from './styles.css'

// Favicons
import CuratedAppleTouchIcon from './favicons/curated/apple-touch-icon.png'
import CuratedFavicon16 from './favicons/curated/favicon-16x16.png'
import CuratedFavicon32 from './favicons/curated/favicon-32x32.png'
import isSupportedBrowser from '#src/services/unsupportedBrowsers'
import PageMetadata from '#src/components/PageMetadata'

class App extends Component<WithAnalyticsProps & WithIdentityProps & RouteComponentProps<{}>> {
  public unlisten: () => void
  private authBroadcastChannel: any = null

  constructor(props: WithAnalyticsProps & WithIdentityProps & RouteComponentProps<{}>) {
    super(props)

    this.unlisten = () => null
  }

  public componentDidMount() {
    // Pageview tracking
    this.unlisten = this.props.history.listen(() => {
      if (this.props.analytics) {
        this.props.analytics.events.trackPageViewed()
      }
    })

    // Initial pageview event & tracking event for if user is coming from referral link
    if (this.props.analytics) {
      const urlParams = new URLSearchParams(window.location.search)
      const entryPoint = urlParams.get('entry_point')
      const medium = urlParams.get('utm_medium')
      if (entryPoint && medium && this.props.analytics) {
        this.props.analytics.events.trackConsumerVisitedFromReferralLink({
          entry_point: entryPoint as ConsumerTracking.EntryPoint,
          medium: medium as ConsumerTracking.ReferralMedium
        })
      }
    }

    this.registerAuthChangeListener()
  }

  public componentDidUpdate(
    prevProps: WithAnalyticsProps & WithIdentityProps & RouteComponentProps<{}>
  ) {
    if (this.props.userId && this.props.userId !== prevProps.userId && this.props.myself) {
      // this.setLocalAcquisitionAttributes(this.props.myself, this.props.analytics)
      this.broadcastOnAuthChange(this.props.myself.user.id)
    } else if (prevProps.myself && !this.props.myself) {
      this.broadcastOnAuthChange(null)
    }
  }

  public componentWillUnmount() {
    if (this.unlisten) {
      this.unlisten()
    }
    if (this.authBroadcastChannel) {
      this.authBroadcastChannel.close()
    }
  }

  private registerAuthChangeListener() {
    // To avoid any authorization errors, when the underlying identity changed,
    // we should redirect the user to the consumer home page.
    const app = this
    const { BroadcastChannel } = require('broadcast-channel')
    if (BroadcastChannel) {
      // @ts-ignore
      this.authBroadcastChannel = new BroadcastChannel('deal-consumer-auth')
      this.authBroadcastChannel.onmessage = (message: any) => {
        const currentUserId =
          app.props.myself && app.props.myself.user ? app.props.myself!.user!.id : null
        const newUserId = message.userId
        if (currentUserId !== newUserId) {
          const consumer = config.get('consumer')
          window.location.href = `${consumer.protocol}://${consumer.host}:${consumer.port}`
        }
      }
    }
    this.broadcastOnAuthChange(this.props.myself ? this.props.myself.user.id : null)
  }

  private broadcastOnAuthChange(newUserId: string | null) {
    // when underlying identity changed, broadcast the change.  All other tabs
    // with the same origin will receive the message and take appropriate action
    if (this.authBroadcastChannel) {
      this.authBroadcastChannel.postMessage({ userId: newUserId })
    }
  }

  public render() {
    return (
      <DepartmentProvider>
        <ExpertConversationStartersContextProvider>
          {/* Default metadata. These can be overridden by more deeply nested instances of PageMetadata. */}
          <PageMetadata
            imageUrl={DefaultOpenGraphImage}
            type="website"
            twitterCardType="summary_large_image"
          />
          <DepartmentOpenGraph />
          <Helmet>
            <meta name="keywords" content="deal deals" />
            <meta name="msapplication-TileColor" content="#ffffff" />
            <meta name="theme-color" content="#ffffff" />
            <link rel="apple-touch-icon" sizes="180x180" href={CuratedAppleTouchIcon} />
            <link rel="icon" type="image/png" sizes="32x32" href={CuratedFavicon32} />
            <link rel="icon" type="image/png" sizes="16x16" href={CuratedFavicon16} />
          </Helmet>

          <ExpertProfileDraftFullPageTakeover />

          <ToastContainer
            position="bottom-right"
            autoClose={5000}
            hideProgressBar={true}
            newestOnTop={false}
            closeOnClick={true}
            pauseOnHover={false}
            className={styles.toast}
          />

          <UserAgentConsumer>
            {userAgent => {
              return (
                <>
                  {!isSupportedBrowser(userAgent) && (
                    <UnsupportedBrowserBanner
                      text="Unsupported browser, some things might not work."
                      linkText="View compatible browsers"
                    />
                  )}
                </>
              )
            }}
          </UserAgentConsumer>

          <ScrollToTop>
            <Router />
          </ScrollToTop>
        </ExpertConversationStartersContextProvider>
      </DepartmentProvider>
    )
  }
}

export default hot(withIdentity(withAnalytics(withRouter(App))))
